import { useState, useEffect } from 'react';
import { Users, Activity, Key, TrendingUp, RefreshCw } from 'lucide-react';
import { Card, CardHeader, Button } from '@/components/common';
import { StatCard, ActivityFeed, LoginChart, SystemHealth } from '@/components/dashboard';
import { useAuth } from '@/contexts/AuthContext';
import { formatNumber } from '@/utils/format';
import { analyticsService } from '@/services/analyticsService';
import type { ActivityLog, ChartDataPoint } from '@/types';

export function DashboardPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    oauthClients: 0,
    apiRequests: 0,
  });
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [loginData, setLoginData] = useState<ChartDataPoint[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch dashboard stats
      const statsData = await analyticsService.getDashboardStats();
      setStats({
        totalUsers: statsData.total_users || 0,
        activeUsers: statsData.active_users || 0,
        oauthClients: statsData.oauth_clients || 0,
        apiRequests: statsData.api_requests || 0,
      });

      // Fetch activity logs
      const activityData = await analyticsService.getActivityLogs({ per_page: 5 });
      setActivities(activityData);

      // Fetch login trends
      const trendsData = await analyticsService.getLoginTrends({ days: 7 });
      if (trendsData && Array.isArray(trendsData)) {
        const chartData = trendsData.map((item: any) => ({
          name: item.day || item.name,
          value: item.logins || item.value,
          logins: item.logins || 0,
          failed: item.failed || 0,
        }));
        setLoginData(chartData);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Fallback to empty data
      setActivities([]);
      setLoginData([
        { name: 'Mon', value: 0, logins: 0, failed: 0 },
        { name: 'Tue', value: 0, logins: 0, failed: 0 },
        { name: 'Wed', value: 0, logins: 0, failed: 0 },
        { name: 'Thu', value: 0, logins: 0, failed: 0 },
        { name: 'Fri', value: 0, logins: 0, failed: 0 },
        { name: 'Sat', value: 0, logins: 0, failed: 0 },
        { name: 'Sun', value: 0, logins: 0, failed: 0 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const healthServices = [
    {
      name: 'Database',
      status: 'healthy' as const,
      message: 'All database connections are operational',
      lastCheck: '2 minutes ago',
    },
    {
      name: 'Cache (Redis)',
      status: 'healthy' as const,
      message: 'Cache hit rate: 94.3%',
      lastCheck: '1 minute ago',
    },
    {
      name: 'OAuth Server',
      status: 'healthy' as const,
      message: 'OAuth server responding normally',
      lastCheck: '30 seconds ago',
    },
    {
      name: 'Email Service',
      status: 'healthy' as const,
      message: 'Mail queue processing normally',
      lastCheck: '5 minutes ago',
    },
  ];

  const handleRefresh = () => {
    setLastRefresh(new Date());
    fetchDashboardData();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">
            Welcome back, {user?.name?.split(' ')[0] || 'Admin'}! 👋
          </h1>
          <p className="text-secondary-600 mt-1">
            Here's what's happening with your SSO server today.
          </p>
        </div>
        <Button
          variant="outline"
          icon={<RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={formatNumber(stats.totalUsers)}
          icon={Users}
          trend={{ value: '+12.5%', isPositive: true }}
          iconColor="text-blue-600"
          iconBgColor="bg-blue-100"
        />
        <StatCard
          title="Active Sessions"
          value={formatNumber(stats.activeUsers)}
          icon={Activity}
          trend={{ value: '+8.3%', isPositive: true }}
          iconColor="text-green-600"
          iconBgColor="bg-green-100"
        />
        <StatCard
          title="OAuth Clients"
          value={formatNumber(stats.oauthClients)}
          icon={Key}
          trend={{ value: '+3.2%', isPositive: true }}
          iconColor="text-purple-600"
          iconBgColor="bg-purple-100"
        />
        <StatCard
          title="API Requests"
          value={formatNumber(stats.apiRequests)}
          icon={TrendingUp}
          trend={{ value: '+15.7%', isPositive: true }}
          iconColor="text-orange-600"
          iconBgColor="bg-orange-100"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Login Activity Chart */}
        <Card>
          <CardHeader
            title="Login Activity"
            description="Successful and failed login attempts this week"
          />
          <LoginChart data={loginData} loading={loading} />
        </Card>

        {/* Recent Activity Feed */}
        <Card>
          <CardHeader
            title="Recent Activity"
            description="Latest actions performed by users"
          />
          <div className="max-h-[350px] overflow-y-auto custom-scrollbar">
            <ActivityFeed activities={activities} loading={loading} />
          </div>
        </Card>
      </div>

      {/* System Health */}
      <Card>
        <CardHeader
          title="System Health"
          description="Real-time status of critical services"
        />
        <SystemHealth services={healthServices} loading={loading} />
      </Card>

      {/* Last Refresh */}
      <div className="text-center text-sm text-secondary-500">
        Last updated: {lastRefresh.toLocaleTimeString()}
      </div>
    </div>
  );
}
